import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon, SkeletonSet } from '@cwos/ui-admin';
import {
  groupThreads,
  searchThreads,
  type IntelligenceThread,
  type ThreadsClient,
} from './threads.js';
import { LoadFailure } from '../../LoadFailure.js';

/**
 * The conversation sidebar: New chat, search, and every thread this account
 * has, grouped by when it was last touched.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT IS AND IS NOT LOCAL STATE HERE.
 *
 * Local: which thread is open, what is typed in the search box, which row has
 * its menu open, which row is mid-rename, which row is awaiting a delete
 * confirmation. All of it is "where the person is right now", all of it is
 * worthless a second after they leave, and none of it is written anywhere.
 *
 * Server: the threads, their titles, their pinned flags and their timestamps.
 * Every one of those is read from {@link ThreadsClient} and written back
 * through it, and the list is invalidated after each mutation so the sidebar
 * shows the server's answer rather than its own optimism. See `threads.ts` for
 * why none of it may live in the browser.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY DELETE CONFIRMS INLINE RATHER THAN IN A `window.confirm`.
 *
 * A native confirm is untestable in jsdom, unstyleable, and — the reason that
 * actually matters — it does not say WHAT is being deleted. The inline
 * confirmation names the thread by its title and offers the two buttons next
 * to the row they act on. Archive does not confirm, because it is reversible
 * server-side; delete does, because it is not.
 */

export interface HistoryPaneProps {
  /**
   * The durable thread API, or `undefined` while L141's client is unbound.
   * Undefined renders an honest not-available-yet note where the list goes —
   * never a browser-local stand-in. See `threadsClient.ts`.
   */
  readonly client: ThreadsClient | undefined;
  /** The open conversation, so its row can be marked current. */
  readonly activeThreadId: string | undefined;
  /**
   * Hands back the WHOLE row, not just its id.
   *
   * The host needs the thread's `title` for the header and its `threadId` to
   * append the next turn, and the pane is already holding the row the person
   * clicked. Passing an id would make the host look up something it was
   * standing next to, or — worse — carry a half-filled thread around until the
   * real one arrived.
   *
   * (This paragraph used to say the host needed the thread's `sessionId`. That
   * field is gone: the port was written before the durable thread API landed
   * and guessed a per-thread session handle the server never had. The sentence
   * outlived the field by exactly as long as nothing typechecked it.)
   */
  readonly onSelectThread: (thread: IntelligenceThread) => void;
  readonly onNewChat: () => void;
  /**
   * The clock the date grouping reads. Injected so "Today" is a fact a test
   * can pin rather than whatever day the suite happens to run on.
   */
  readonly now?: Date;
}

const THREADS_KEY = ['intelligence', 'threads'] as const;

export function HistoryPane({
  client,
  activeThreadId,
  onSelectThread,
  onNewChat,
  now,
}: HistoryPaneProps) {
  const [query, setQuery] = useState('');

  return (
    <aside className="ap-history" aria-label="Conversations">
      <div className="ap-history-head">
        <button type="button" className="ap-newchat" onClick={onNewChat}>
          <Icon name="plus" size={15} />
          New chat
        </button>
        <div className="ap-history-search">
          <Icon name="search" size={14} className="ap-history-search-icon" />
          <label className="ap-sr-only" htmlFor="ap-thread-search">
            Search conversations
          </label>
          <input
            id="ap-thread-search"
            type="search"
            className="ap-history-search-input"
            placeholder="Search conversations"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>

      {client === undefined ? (
        <ThreadsUnavailable />
      ) : (
        <ThreadList
          client={client}
          query={query}
          activeThreadId={activeThreadId}
          onSelectThread={onSelectThread}
          now={now}
        />
      )}
    </aside>
  );
}

/**
 * What the pane says when no durable thread API is bound.
 *
 * Deliberately not an error and not a spinner: nothing failed and nothing is
 * loading. A capability is not switched on in this deployment yet, which is
 * the same category of fact as an unconfigured provider, and it gets the same
 * treatment — say so plainly, offer no retry, and leave New chat working,
 * because starting a fresh conversation never needed the server's list.
 */
function ThreadsUnavailable() {
  // No `role="status"`: this is static copy that is present from the first
  // paint, not a live update. A status region here would also make the pane a
  // second announcing region competing with the one that matters — the "Working
  // on your question…" state in the conversation beside it.
  return (
    <p className="rn-muted ap-history-empty">
      Saved conversations aren’t switched on for this deployment yet. You can still ask anything —
      this one just won’t be here when you come back.
    </p>
  );
}

function ThreadList({
  client,
  query,
  activeThreadId,
  onSelectThread,
  now,
}: {
  client: ThreadsClient;
  query: string;
  activeThreadId: string | undefined;
  onSelectThread: (thread: IntelligenceThread) => void;
  now: Date | undefined;
}) {
  const queryClient = useQueryClient();
  const threadsQuery = useQuery({
    queryKey: THREADS_KEY,
    queryFn: () => client.list(),
  });

  /**
   * The last write that did not happen, or null.
   *
   * WITHOUT THIS, A FAILED WRITE WAS INVISIBLE. Every mutation below had an
   * `onSuccess` and no `onError`, and the row closes its edit or confirm mode
   * as soon as the handler fires — so a rename that 500'd put the OLD title
   * back with nothing said, and a delete that failed left the row exactly
   * where it was. Neither renders as a false success, which is the worse
   * defect; both render as nothing, which leaves a person pressing the same
   * button again and wondering whether the first press did something.
   *
   * The sentence names the act and states what did NOT change, because "we
   * couldn't delete that" and "we deleted it and could not tell you" are
   * different facts and only one of them is true here: the list is re-read
   * from the server on success and untouched on failure, so nothing local ever
   * diverged from what the server holds.
   */
  const [failedAct, setFailedAct] = useState<string | null>(null);

  const invalidate = () => {
    setFailedAct(null);
    void queryClient.invalidateQueries({ queryKey: THREADS_KEY });
  };

  const rename = useMutation({
    mutationFn: (input: { threadId: string; title: string }) => client.rename(input),
    onSuccess: invalidate,
    onError: () => setFailedAct('rename that conversation'),
  });
  const setPinned = useMutation({
    mutationFn: (input: { threadId: string; pinned: boolean }) => client.setPinned(input),
    onSuccess: invalidate,
    onError: () => setFailedAct('change that pin'),
  });
  const archive = useMutation({
    mutationFn: (input: { threadId: string }) => client.archive(input),
    onSuccess: invalidate,
    onError: () => setFailedAct('archive that conversation'),
  });
  const remove = useMutation({
    mutationFn: (input: { threadId: string }) => client.remove(input),
    onSuccess: invalidate,
    onError: () => setFailedAct('delete that conversation'),
  });

  if (threadsQuery.isLoading) {
    return (
      <div className="ap-history-loading">
        <SkeletonSet surface="table" rows={4} />
      </div>
    );
  }
  if (threadsQuery.isError) {
    return (
      <LoadFailure
        error={threadsQuery.error}
        message="We couldn’t load your conversations just now."
        onRetry={() => void threadsQuery.refetch()}
      />
    );
  }

  const all = threadsQuery.data?.threads ?? [];
  const matching = searchThreads(all, query);

  if (all.length === 0) {
    return (
      <p className="rn-muted ap-history-empty">
        Your conversations will appear here once you’ve asked something.
      </p>
    );
  }
  if (matching.length === 0) {
    return (
      <p className="rn-muted ap-history-empty" role="status">
        No conversations match “{query.trim()}”.
      </p>
    );
  }

  const groups = groupThreads(matching, (now ?? new Date()).getTime());

  return (
    <div className="ap-history-groups">
      {failedAct !== null ? (
        <p className="ap-history-failed" role="status" aria-live="polite">
          We couldn’t {failedAct}. Nothing changed — the list below is still what the server holds.
        </p>
      ) : null}
      {groups.map((group) => (
        <section key={group.label} className="ap-history-group">
          <h3 className="ap-history-group-label">{group.label}</h3>
          <ul className="ap-history-list" aria-label={group.label}>
            {group.threads.map((thread) => (
              <ThreadRow
                key={thread.threadId}
                thread={thread}
                active={thread.threadId === activeThreadId}
                busy={
                  rename.isPending || setPinned.isPending || archive.isPending || remove.isPending
                }
                onSelect={() => onSelectThread(thread)}
                onRename={(title) => rename.mutate({ threadId: thread.threadId, title })}
                onTogglePin={() =>
                  setPinned.mutate({ threadId: thread.threadId, pinned: !thread.pinned })
                }
                onArchive={() => archive.mutate({ threadId: thread.threadId })}
                onDelete={() => remove.mutate({ threadId: thread.threadId })}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** What a row is showing instead of its title, when it is not showing its title. */
type RowMode = 'idle' | 'renaming' | 'confirming-delete';

function ThreadRow({
  thread,
  active,
  busy,
  onSelect,
  onRename,
  onTogglePin,
  onArchive,
  onDelete,
}: {
  thread: IntelligenceThread;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onTogglePin: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const [mode, setMode] = useState<RowMode>('idle');
  const [draftTitle, setDraftTitle] = useState(thread.title);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  if (mode === 'renaming') {
    const trimmed = draftTitle.trim();
    return (
      <li className="ap-history-row ap-history-row-editing">
        <label className="ap-sr-only" htmlFor={`ap-rename-${thread.threadId}`}>
          Rename conversation
        </label>
        <input
          id={`ap-rename-${thread.threadId}`}
          className="ap-history-rename-input"
          value={draftTitle}
          autoFocus
          onChange={(event) => setDraftTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setMode('idle');
          }}
        />
        <div className="ap-history-row-actions">
          <button
            type="button"
            className="msg-primary ap-history-mini"
            // An empty title is not a title. The server would have to invent
            // one, and a row labelled "Untitled" is worse than the one the
            // person is looking at.
            disabled={trimmed === '' || busy}
            onClick={() => {
              onRename(trimmed);
              setMode('idle');
            }}
          >
            Save
          </button>
          <button
            type="button"
            className="rn-btn-quiet ap-history-mini"
            onClick={() => {
              setDraftTitle(thread.title);
              setMode('idle');
            }}
          >
            Cancel
          </button>
        </div>
      </li>
    );
  }

  if (mode === 'confirming-delete') {
    return (
      <li className="ap-history-row ap-history-row-confirm">
        <p className="ap-history-confirm-text">Delete “{thread.title}”? This can’t be undone.</p>
        <div className="ap-history-row-actions">
          <button
            type="button"
            className="msg-primary ap-history-mini ap-history-danger"
            disabled={busy}
            onClick={() => {
              onDelete();
              setMode('idle');
            }}
          >
            Delete
          </button>
          <button
            type="button"
            className="rn-btn-quiet ap-history-mini"
            onClick={() => setMode('idle')}
          >
            Cancel
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={active ? 'ap-history-row ap-history-row-active' : 'ap-history-row'}>
      <button
        type="button"
        className="ap-history-open"
        aria-current={active ? 'true' : undefined}
        onClick={onSelect}
      >
        {thread.pinned ? (
          <Icon name="dot" size={10} className="ap-history-pinmark" aria-hidden="true" />
        ) : null}
        <span className="ap-history-title">{thread.title}</span>
      </button>

      <div className="ap-history-menu">
        <button
          type="button"
          className="ap-history-menu-toggle"
          aria-expanded={menuOpen}
          aria-label={`Actions for ${thread.title}`}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <Icon name="ellipsis" size={16} />
        </button>
        {menuOpen ? (
          <ul className="ap-history-menu-list" aria-label={`Actions for ${thread.title}`}>
            <li>
              <button
                type="button"
                className="ap-history-menu-item"
                onClick={() => {
                  setDraftTitle(thread.title);
                  setMode('renaming');
                  closeMenu();
                }}
              >
                Rename
              </button>
            </li>
            <li>
              <button
                type="button"
                className="ap-history-menu-item"
                disabled={busy}
                onClick={() => {
                  onTogglePin();
                  closeMenu();
                }}
              >
                {thread.pinned ? 'Unpin' : 'Pin'}
              </button>
            </li>
            <li>
              <button
                type="button"
                className="ap-history-menu-item"
                disabled={busy}
                onClick={() => {
                  onArchive();
                  closeMenu();
                }}
              >
                Archive
              </button>
            </li>
            <li>
              <button
                type="button"
                className="ap-history-menu-item ap-history-danger"
                onClick={() => {
                  setMode('confirming-delete');
                  closeMenu();
                }}
              >
                Delete
              </button>
            </li>
          </ul>
        ) : null}
      </div>
    </li>
  );
}
